/**
 * Knowledge Store Hooks
 *
 * Query and aggregate knowledge state data for Map of Knowledge visualization.
 * Provides hooks for region completion, granularity levels, and category-specific knowledge.
 */

import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/db/database";
import type { Category } from "@/db/types";
import { deduplicateKnowledgeStates } from "@/utils/knowledgeMapUtils";

/**
 * Get all knowledge states for a specific category
 */
export function useKnowledgeByCategory(category: Category) {
  return useLiveQuery(
    () => db.knowledge_state.where("category").equals(category).toArray(),
    [category]
  );
}

/**
 * Get all knowledge states across all categories
 */
export function useAllKnowledge() {
  return useLiveQuery(() => db.knowledge_state.toArray());
}

/**
 * Get knowledge state for a specific region and category
 */
export function useRegionKnowledge(
  category: Category,
  region: string,
  granularityLevel: number
) {
  return useLiveQuery(
    () =>
      db.knowledge_state
        .where("[category+region+granularityLevel]")
        .equals([category, region, granularityLevel])
        .first(),
    [category, region, granularityLevel]
  );
}

/**
 * Get highest granularity level unlocked for a region across all categories
 */
export function useRegionMaxGranularity(region: string) {
  return useLiveQuery(async () => {
    const states = await db.knowledge_state
      .where("region")
      .equals(region)
      .toArray();

    if (states.length === 0) return 0;

    return Math.max(...states.map((s) => s.granularityLevel));
  }, [region]);
}

/**
 * Get completion stats for a category
 */
export function useCategoryStats(category: Category) {
  return useLiveQuery(async () => {
    const states = await db.knowledge_state
      .where("category")
      .equals(category)
      .toArray();

    if (states.length === 0) {
      return {
        totalRegions: 0,
        totalItems: 0,
        lastActivityDate: null,
        itemsNeedingReview: 0,
        completedRegions: 0,
        perfectRegions: 0,
        averageCompletion: 0,
      };
    }

    // Deduplicate overlapping quizzes to avoid double-counting
    const deduplicated = deduplicateKnowledgeStates(states, category);

    // Total regions (use deduplicated count)
    const totalRegions = deduplicated.length;

    // Total items learned: Only count items from 100% quizzes (deduplicated)
    // This matches the "items learned" logic: only count mastered items
    const totalItems = deduplicated
      .filter(s => s.achievedPerfection)
      .reduce((sum, s) => sum + s.totalPrompts, 0);

    // Last activity (most recent perfect completion or last updated) - use all states for this
    const lastActivityDate = states
      .map((s) => s.perfectCompletionDate || s.lastUpdated)
      .filter((d): d is string => d !== null)
      .sort()
      .reverse()[0] || null;

    // Items needing review (regions that are yellow/orange/red) - use deduplicated states
    const itemsNeedingReview = deduplicated
      .filter((s) => {
        if (!s.achievedPerfection || !s.perfectCompletionDate) return false;
        const daysSince = (Date.now() - new Date(s.perfectCompletionDate).getTime()) / (1000 * 60 * 60 * 24);
        return daysSince >= 7; // 1+ week old (yellow, orange, or red)
      })
      .reduce((sum, s) => sum + s.totalPrompts, 0);

    const completedRegions = deduplicated.filter(
      (s) => s.completionRate >= 100
    ).length;
    const perfectRegions = deduplicated.filter((s) => s.achievedPerfection).length;
    const averageCompletion =
      deduplicated.reduce((sum, s) => sum + s.completionRate, 0) / deduplicated.length / 100;

    return {
      totalRegions,
      totalItems,
      lastActivityDate,
      itemsNeedingReview,
      completedRegions,
      perfectRegions,
      averageCompletion,
    };
  }, [category]);
}

/**
 * Get combined knowledge map showing highest granularity per region
 * Returns full KnowledgeState objects (not simplified) for accurate display
 * Note: This also applies deduplication to prevent double-counting overlapping quizzes
 */
export function useCombinedKnowledgeMap() {
  return useLiveQuery(async () => {
    const allStates = await db.knowledge_state.toArray();

    // First, deduplicate overlapping quizzes within the same region
    const deduplicated = deduplicateKnowledgeStates(allStates, 'combined');

    // Then, group by region and find highest granularity with best completion
    const regionMap = new Map<string, any>();

    deduplicated.forEach((state) => {
      const existing = regionMap.get(state.region);

      // Prioritize: higher granularity > 100% completion > more prompts > higher completion
      const isBetter =
        !existing ||
        state.granularityLevel > existing.granularityLevel ||
        (state.granularityLevel === existing.granularityLevel &&
          (state.achievedPerfection && !existing.achievedPerfection)) ||
        (state.granularityLevel === existing.granularityLevel &&
          state.achievedPerfection === existing.achievedPerfection &&
          state.totalPrompts > existing.totalPrompts) ||
        (state.granularityLevel === existing.granularityLevel &&
          state.achievedPerfection === existing.achievedPerfection &&
          state.totalPrompts === existing.totalPrompts &&
          state.completionRate > existing.completionRate);

      if (isBetter) {
        // Store the FULL state object, not a simplified version
        regionMap.set(state.region, state);
      }
    });

    return Array.from(regionMap.values());
  });
}

/**
 * Check if a region has unlocked higher granularity
 * (100% completion on unscoped quiz at current level)
 */
export function useIsGranularityUnlocked(
  category: Category,
  region: string,
  granularityLevel: number
): boolean {
  const knowledge = useRegionKnowledge(category, region, granularityLevel);

  if (!knowledge) return false;

  // Check if this level is 100% complete and was done without scope
  // (This is simplified - full implementation would check quiz_sessions for unscoped completion)
  return knowledge.completionRate >= 100 && knowledge.achievedPerfection;
}

/**
 * Get suggested regions to study next based on current knowledge
 */
export function useSuggestedRegions(category: Category, limit: number = 3) {
  return useLiveQuery(async () => {
    const allStates = await db.knowledge_state
      .where("category")
      .equals(category)
      .toArray();

    if (allStates.length === 0) return [];

    // Priority: Partially completed regions (10% < completion < 90%)
    // These are regions user has started but not mastered
    const partial = allStates
      .filter((s) => s.completionRate > 10 && s.completionRate < 90)
      .sort((a, b) => b.completionRate - a.completionRate);

    // If no partial, suggest completely unknown regions
    const unknown = allStates
      .filter((s) => s.completionRate === 0)
      .slice(0, limit);

    const suggestions = partial.length > 0 ? partial : unknown;

    return suggestions.slice(0, limit).map((s) => ({
      region: s.region,
      completionRate: s.completionRate,
      reason:
        s.completionRate > 0
          ? `${Math.round(s.completionRate)}% complete - keep going!`
          : "New region to explore",
    }));
  }, [category, limit]);
}

/**
 * Get overall knowledge statistics across all categories
 */
export function useOverallKnowledgeStats() {
  return useLiveQuery(async () => {
    const states = await db.knowledge_state.toArray();

    if (states.length === 0) {
      return {
        totalRegions: 0,
        totalItems: 0,
        lastActivityDate: null,
        itemsNeedingReview: 0,
      };
    }

    // For display: deduplicate using combined mode (1 quiz per geography)
    const deduplicated = deduplicateKnowledgeStates(states, 'combined');

    // Total regions (deduplicated)
    const totalRegions = deduplicated.length;

    // Total items learned: Pick biggest 100% per category per geography, then sum
    // This is different from display deduplication - we count ALL perfect categories
    // Example: Spain Admin @ 100% AND Spain Phone @ 100% both count
    const perfectStatesOnly = states.filter(s => s.achievedPerfection);

    // Group by geography + category
    const categoryGeographyGroups = new Map<string, typeof states>();
    perfectStatesOnly.forEach(state => {
      const geography = state.geography || state.region;
      const category = state.category || 'unknown';
      const groupKey = `${geography}:${category}`;

      const existing = categoryGeographyGroups.get(groupKey) || [];
      existing.push(state);
      categoryGeographyGroups.set(groupKey, existing);
    });

    // For each group, pick the biggest quiz and sum totalPrompts
    let totalItems = 0;
    categoryGeographyGroups.forEach((groupStates) => {
      // Pick the biggest (most totalPrompts)
      // Tie-breaker: If same totalPrompts, pick most recent completion
      const sorted = groupStates.sort((a, b) => {
        if (b.totalPrompts !== a.totalPrompts) {
          return b.totalPrompts - a.totalPrompts;
        }
        const dateA = a.perfectCompletionDate ? new Date(a.perfectCompletionDate).getTime() : 0;
        const dateB = b.perfectCompletionDate ? new Date(b.perfectCompletionDate).getTime() : 0;
        return dateB - dateA; // Newer is better
      });
      const biggest = sorted[0];
      if (biggest) {
        totalItems += biggest.totalPrompts;
      }
    });

    // Last activity (most recent perfect completion or last updated) - use all states for this
    const lastActivityDate = states
      .map((s) => s.perfectCompletionDate || s.lastUpdated)
      .filter((d): d is string => d !== null)
      .sort()
      .reverse()[0] || null;

    // Items needing review (regions that are yellow/orange/red) - use deduplicated states
    const itemsNeedingReview = deduplicated
      .filter((s) => {
        if (!s.achievedPerfection || !s.perfectCompletionDate) return false;
        const daysSince = (Date.now() - new Date(s.perfectCompletionDate).getTime()) / (1000 * 60 * 60 * 24);
        return daysSince >= 7; // 1+ week old (yellow, orange, or red)
      })
      .reduce((sum, s) => sum + s.totalPrompts, 0);

    return {
      totalRegions,
      totalItems,
      lastActivityDate,
      itemsNeedingReview,
    };
  }, []);
}
